import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { CheckCircle, Search, Users } from "lucide-react";
import { getUsersByRole, createTeam } from "../../../api/common";
import SubmitButton from "../../../Components/Button";
import Alert from "../../../Components/AlertPopup";
import styles from "./style.module.scss";

interface Member {
  userId: number;
  name: string;
  email: string;
  roleType: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
}

const ROLE_BADGE_STYLES: Record<string, { border: string; color: string, backgroundColor: string }> = {
  "Lead Auditor": { backgroundColor: "#EFEAF6", border: "#102250", color: "#102250" },
  Auditor: { backgroundColor: "#FFF4EA", border: "#EA580C", color: "#EA580C" },
};

const AddTeamModal: FC<Props> = ({
  show,
  onClose,
  onSaveSuccess,
}) => {
  const [teamName, setTeamName] = useState("");
  const [activeTab, setActiveTab] = useState<"lead" | "auditor">("lead");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [selectedAuditorIds, setSelectedAuditorIds] = useState<number[]>([]);
  const [leadAuditors, setLeadAuditors] = useState<Member[]>([]);
  const [auditors, setAuditors] = useState<Member[]>([]);
  const [saving, setSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  useEffect(() => {
    if (show) {
      fetchMembers();
    }
  }, [show]);

  const fetchMembers = async () => {
    try {
      const [leadRes, auditorRes] = await Promise.all([
        getUsersByRole("LEAD_AUDITOR"),
        getUsersByRole("AUDITOR"),
      ]);
      if (leadRes && Array.isArray(leadRes)) setLeadAuditors(leadRes);
      if (auditorRes && Array.isArray(auditorRes)) setAuditors(auditorRes);
    } catch {
      // keep empty
    }
  };

  const currentList = activeTab === "lead" ? leadAuditors : auditors;
  const selectedIds = activeTab === "lead" ? selectedLeadIds : selectedAuditorIds;

  const filteredMembers = useMemo(() => {
    if (!searchTerm.trim()) return currentList;
    const term = searchTerm.toLowerCase();
    return currentList.filter((m) => m.name.toLowerCase().includes(term));
  }, [currentList, searchTerm]);

  const toggleMember = (userId: number) => {
    if (activeTab === "lead") {
      setSelectedLeadIds((prev) =>
        prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
      );
    } else {
      setSelectedAuditorIds((prev) =>
        prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
      );
    }
  };

  const handleClose = () => {
    setTeamName("");
    setActiveTab("lead");
    setSearchTerm("");
    setSelectedLeadIds([]);
    setSelectedAuditorIds([]);
    setShowSuccessAlert(false);
    setShowErrorAlert(false);
    onClose();
  };

  const handleCreate = async () => {
    setSaving(true);
    setShowSuccessAlert(false);
    setShowErrorAlert(false);
    try {
      const payload = {
        teamName,
        leadAuditorIds: selectedLeadIds,
        auditorIds: selectedAuditorIds,
      };
      await createTeam(payload);
      onSaveSuccess?.();
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.response?.data || "Failed to create team. Please try again.";
      setAlertMessage(msg);
      setShowErrorAlert(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSuccessAlertClose = () => {
    setShowSuccessAlert(false);
    handleClose();
  };

  const isFormValid = teamName.trim() !== "" && (selectedLeadIds.length > 0 || selectedAuditorIds.length > 0);

  const getBadgeStyle = (role: string) => {
    return ROLE_BADGE_STYLES[role] || {  border: "#475569", color: "#475569",backgroundColor: "#475569" };
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className={styles.modal}>
      <Modal.Header closeButton>
        <Modal.Title className={styles.modalTitle}>
          <Users size={20} className={styles.titleIcon} />
          Create New Team
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert
          show={showSuccessAlert}
          onClose={handleSuccessAlertClose}
          message={alertMessage}
        />
        <Alert
          type="error"
          show={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
          message={alertMessage}
        />

        {/* Team Name */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>
            Team Name<span className={styles.required}>*</span>
          </label>
          <Form.Control
            type="text"
            placeholder="e.g., Risk Audit"
            value={teamName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setTeamName(e.target.value)}
            className={styles.teamNameInput}
          />
        </div>

        {/* Select Team Members */}
        <div className={styles.sectionTitle}>Select Team Members</div>

        {/* Tabs */}
        <div className={styles.memberTabs}>
          <button
            className={`${styles.memberTab} ${activeTab === "lead" ? styles.memberTabActive : ""}`}
            onClick={() => { setActiveTab("lead"); setSearchTerm(""); }}
          >
            Lead Auditors ({leadAuditors.length})
          </button>
          <button
            className={`${styles.memberTab} ${activeTab === "auditor" ? styles.memberTabActive : ""}`}
            onClick={() => { setActiveTab("auditor"); setSearchTerm(""); }}
          >
            Auditors ({auditors.length})
          </button>
        </div>

        {/* Search */}
        <div className={styles.searchBox}>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <Search size={16} className={styles.searchIcon} />
        </div>

        {/* Member List */}
        <div className={styles.memberList}>
          {filteredMembers.length > 0 ? (
            filteredMembers.map((member) => {
              const displayRole = activeTab === "lead" ? "Lead Auditor" : "Auditor";
              const badge = getBadgeStyle(displayRole);
              const isSelected = selectedIds.includes(member.userId);
              return (
                <div
                  key={member.userId}
                  className={`${styles.memberRow} ${isSelected ? styles.memberRowSelected : ""}`}
                  style={isSelected ? { borderLeftColor: badge.color } : undefined}
                  onClick={() => toggleMember(member.userId)}
                >
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>{member.name}</span>
                    <span
                      className={styles.roleBadge}
                      style={{ borderColor: badge.border, color: badge.color ,backgroundColor: badge.backgroundColor}}
                    >
                      {displayRole}
                    </span>
                  </div>
                  {isSelected && (
                    <CheckCircle size={20} style={{ color: badge.color }} />
                  )}
                </div>
              );
            })
          ) : (
            <div className={styles.noMembers}>No members found</div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <SubmitButton type="secondary" onClick={handleClose} className={styles.cancelBtn}>
          Cancel
        </SubmitButton>
        <SubmitButton
          onClick={handleCreate}
          className={styles.createBtn}
          disabled={!isFormValid || saving}
        >
          {saving ? "Creating..." : "Create Team"}
        </SubmitButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTeamModal;